import React, { useState, useRef, useEffect } from 'react';
import { Calendar, ChevronUp, ChevronDown } from 'lucide-react';

// ── Helpers ───────────────────────────────────────────────────────────────────

const toInputDate = (date) => {
    if (!date) return '';
    const d = new Date(date);
    if (Number.isNaN(d.getTime())) return '';
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const dateFromInputVal = (val) => {
    if (!val) return null;
    const d = new Date(val);
    return Number.isNaN(d.getTime()) ? null : d;
};

// ── Component ─────────────────────────────────────────────────────────────────

/**
 * CustomDatePickerDashboard
 *
 * Props:
 *  @param {string}   value     - Selected date in "YYYY-MM-DD" format
 *  @param {Function} onChange  - Callback: (dateString: "YYYY-MM-DD") => void
 *  @param {string}   label     - Placeholder label shown when no date is selected
 */
const CustomDatePickerDashboard = ({ value, onChange, label }) => {
    const [showPicker, setShowPicker] = useState(false);
    const [pickerMode, setPickerMode] = useState('day'); // 'day' | 'month' | 'year'
    const [viewDate, setViewDate] = useState(() => dateFromInputVal(value) || new Date());
    const pickerRef = useRef(null);

    // Sync viewDate when value changes externally
    useEffect(() => {
        const d = dateFromInputVal(value);
        if (d) setViewDate(d);
    }, [value]);

    // Close on outside click
    useEffect(() => {
        const handler = (e) => {
            if (pickerRef.current && !pickerRef.current.contains(e.target)) {
                setShowPicker(false);
                setPickerMode('day');
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const selectedDate = dateFromInputVal(value);

    // ── Generators ────────────────────────────────────────────────────────────

    const generateDays = () => {
        const year = viewDate.getFullYear();
        const month = viewDate.getMonth();
        const total = new Date(year, month + 1, 0).getDate();
        const days = [];
        for (let i = 1; i <= total; i++) days.push(new Date(year, month, i));
        return days;
    };

    const generateMonths = () => {
        const months = [];
        for (let i = 0; i < 12; i++) months.push(new Date(viewDate.getFullYear(), i, 1));
        return months;
    };

    const generateYears = () => {
        const cy = viewDate.getFullYear();
        const years = [];
        for (let y = cy - 10; y <= cy + 10; y++) years.push(y);
        return years;
    };

    // ── Navigation ────────────────────────────────────────────────────────────

    const handleNav = (dir) => {
        const d = new Date(viewDate);
        if (pickerMode === 'day') d.setMonth(d.getMonth() + dir);
        else if (pickerMode === 'month') d.setFullYear(d.getFullYear() + dir);
        else d.setFullYear(d.getFullYear() + dir * 10);
        setViewDate(d);
    };

    const handleDaySelect = (day) => {
        onChange(toInputDate(day));
        setShowPicker(false);
        setPickerMode('day');
    };

    const handleTodayClick = () => {
        const today = new Date();
        setViewDate(today);
        onChange(toInputDate(today));
        setShowPicker(false);
        setPickerMode('day');
    };

    // ── Header Label ──────────────────────────────────────────────────────────

    const headerLabel = () => {
        if (pickerMode === 'day')
            return `${viewDate.toLocaleDateString('en-US', { month: 'long' })} ${viewDate.getFullYear()}`;
        if (pickerMode === 'month')
            return `${viewDate.getFullYear()}`;
        const yrs = generateYears();
        return `${yrs[0]} – ${yrs[yrs.length - 1]}`;
    };

    // Display label on trigger button
    const displayLabel = selectedDate
        ? selectedDate.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' })
        : label;

    // Leading empty cells for first-of-month offset
    const leadingBlanks = new Date(viewDate.getFullYear(), viewDate.getMonth(), 1).getDay();

    // ── Render ────────────────────────────────────────────────────────────────

    return (
        <div className="ddp-datepicker-container" ref={pickerRef}>

            {/* Trigger Button */}
            <div
                className="ddp-datepicker-trigger"
                onClick={() => { setShowPicker((v) => !v); setPickerMode('day'); }}
            >
                <Calendar className="ddp-datepicker-cal-icon" size={15} />
                <span className="ddp-datepicker-label">{displayLabel}</span>
                {showPicker ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </div>

            {/* Popup */}
            {showPicker && (
                <div className="ddp-datepicker-popup">

                    {/* Header */}
                    <div className="ddp-dp-header">
                        <button className="ddp-dp-nav" onClick={() => handleNav(-1)}>&lt;</button>
                        <div
                            className="ddp-dp-title"
                            onClick={() => {
                                if (pickerMode === 'day') setPickerMode('month');
                                else if (pickerMode === 'month') setPickerMode('year');
                            }}
                        >
                            {headerLabel()}
                        </div>
                        <button className="ddp-dp-nav" onClick={() => handleNav(1)}>&gt;</button>
                    </div>

                    {/* Grid */}
                    <div className="ddp-dp-grid">

                        {/* Day View */}
                        {pickerMode === 'day' && (
                            <>
                                <div className="ddp-dp-weekdays">
                                    {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((d) => (
                                        <div key={d} className="ddp-dp-weekday">{d}</div>
                                    ))}
                                </div>
                                <div className="ddp-dp-days">
                                    {Array.from({ length: leadingBlanks }).map((_, i) => (
                                        <div key={`blank-${i}`} />
                                    ))}
                                    {generateDays().map((day, i) => {
                                        const isToday = day.toDateString() === new Date().toDateString();
                                        const isSelected = selectedDate && day.toDateString() === selectedDate.toDateString();
                                        return (
                                            <div
                                                key={i}
                                                className={`ddp-dp-day${isToday ? ' today' : ''}${isSelected ? ' selected' : ''}`}
                                                onClick={() => handleDaySelect(day)}
                                            >
                                                {day.getDate()}
                                            </div>
                                        );
                                    })}
                                </div>
                            </>
                        )}

                        {/* Month View */}
                        {pickerMode === 'month' && (
                            <div className="ddp-dp-months">
                                {generateMonths().map((m, i) => {
                                    const isSel = m.getMonth() === viewDate.getMonth();
                                    return (
                                        <div
                                            key={i}
                                            className={`ddp-dp-month${isSel ? ' selected' : ''}`}
                                            onClick={() => { setViewDate(m); setPickerMode('day'); }}
                                        >
                                            {m.toLocaleDateString('en-US', { month: 'short' })}
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {/* Year View */}
                        {pickerMode === 'year' && (
                            <div className="ddp-dp-years">
                                {generateYears().map((y, i) => {
                                    const isSel = y === viewDate.getFullYear();
                                    return (
                                        <div
                                            key={i}
                                            className={`ddp-dp-year${isSel ? ' selected' : ''}`}
                                            onClick={() => {
                                                const d = new Date(viewDate);
                                                d.setFullYear(y);
                                                setViewDate(d);
                                                setPickerMode('month');
                                            }}
                                        >
                                            {y}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="ddp-dp-footer">
                        <button className="ddp-dp-today-btn" onClick={handleTodayClick}>Today</button>
                        <button className="ddp-dp-close-btn" onClick={() => { setShowPicker(false); setPickerMode('day'); }}>Close</button>
                    </div>

                </div>
            )}
        </div>
    );
};

export default CustomDatePickerDashboard;